import React, { PureComponent, ReactNode } from 'react';
import { NotAllowedIcon } from '../../icons/NotAllowedIcon';
import { ArrowIcon } from '../../icons/ArrowIcon';
import { SettingFilled } from '@ant-design/icons';
import { Button } from 'antd';
import { ToggleButton } from '../../common/ToggleButton';
import { FilterGrid } from '../../common/FilterGrid';
import './FilterComponent.less';

export type FilterOrientation = 'LTR' | 'RTL' | 'TTB' | 'BTT';

interface FilterComponentProps {
    orientation?: FilterOrientation;
    defaultOrientation?: FilterOrientation;
    onOrientationChange?(orientation: FilterOrientation): void;
    settingsButton: ReactNode;

    reverseEnabled?: boolean;

    enabled?: boolean;
    onEnabledChange?(enabled: boolean): void;
}

interface FilterComponentState {
    checked: boolean;
    enabled: boolean;
    orientation: FilterOrientation;
}

export class FilterComponent extends PureComponent<FilterComponentProps, FilterComponentState> {
    state = {
        checked: false,
        enabled: true,
        orientation: this.props.defaultOrientation || 'LTR',
    };

    isEnabled = () => {
        return this.props.enabled !== undefined ? this.props.enabled : this.state.enabled;
    };
    toggleEnabled = () => {
        const enabled = this.isEnabled();
        if (this.props.enabled !== undefined) {
            // Controlled logic
            this.setState({ checked: false }, () => this.props.onEnabledChange?.(!enabled));
        } else {
            // UnControlled logic
            this.setState({ checked: false, enabled: !enabled }, () => this.props.onEnabledChange?.(!enabled));
        }
    };
    getOrientation = () => {
        return this.props.orientation !== undefined ? this.props.orientation : this.state.orientation;
    };
    reverseOrientation = () => {
        const orientation = this.getOrientation();
        let newOrientation;
        switch (orientation) {
            case 'LTR':
                newOrientation = 'RTL';
                break;
            case 'RTL':
                newOrientation = 'LTR';
                break;
            case 'TTB':
                newOrientation = 'BTT';
                break;
            case 'BTT':
                newOrientation = 'TTB';
                break;
        }
        if (this.props.orientation !== undefined) {
            // Controlled logic
            this.setState({ checked: false }, () => this.props.onOrientationChange?.(newOrientation));
        } else {
            // UnControlled logic
            this.setState({ checked: false, orientation: newOrientation }, () =>
                this.props.onOrientationChange?.(newOrientation),
            );
        }
    };
    getReverseButton = (arrowDirection: 'right' | 'left' | 'up' | 'down') => {
        return (
            <div id="reversed" style={{ visibility: this.state.checked ? 'visible' : 'hidden' }}>
                <Button disabled={!this.props.reverseEnabled} onClick={() => this.reverseOrientation()}>
                    <ArrowIcon direction={arrowDirection} />
                </Button>
            </div>
        );
    };
    getToggleButton = (arrowDirection: 'right' | 'left' | 'up' | 'down') => {
        return (
            <div>
                <ToggleButton checked={this.state.checked} onChange={checked => this.setState({ checked: checked })}>
                    {this.isEnabled() ? (
                        <ArrowIcon direction={arrowDirection} />
                    ) : (
                        <NotAllowedIcon style={{ fontSize: '1.5em' }} />
                    )}
                </ToggleButton>
            </div>
        );
    };
    getSettingsButton = () => {
        return (
            <div
                style={{ color: '#9F9F9F', fontSize: '1.25em', visibility: this.state.checked ? 'visible' : 'hidden' }}>
                {this.props.settingsButton || (
                    <Button style={{ backgroundColor: 'transparent', borderColor: 'transparent' }}>
                        <SettingFilled />
                    </Button>
                )}
            </div>
        );
    };
    getDisableButton = (arrowDirection: 'right' | 'left' | 'up' | 'down') => {
        return (
            <div id="disabledButton" style={{ visibility: this.state.checked ? 'visible' : 'hidden' }}>
                <Button onClick={() => this.toggleEnabled()}>
                    {this.isEnabled() ? <NotAllowedIcon /> : <ArrowIcon direction={arrowDirection} />}
                </Button>
            </div>
        );
    };
    getTopComponent = () => {
        return undefined;
    };
    getTopLeftComponent = () => {
        if (this.getOrientation() === 'LTR') {
            return this.getReverseButton('left');
        } else if (this.getOrientation() === 'TTB') {
            return this.getReverseButton('up');
        }
    };
    getTopRightComponent = () => {
        if (this.getOrientation() === 'RTL') {
            return this.getReverseButton('right');
        } else if (this.getOrientation() === 'TTB') {
            return this.getDisableButton('down');
        }
    };
    getLeftComponent = () => {
        return undefined;
    };
    getCenterComponent = () => {
        if (this.getOrientation() === 'LTR') {
            return this.getToggleButton('right');
        } else if (this.getOrientation() === 'RTL') {
            return this.getToggleButton('left');
        } else if (this.getOrientation() === 'TTB') {
            return this.getToggleButton('down');
        } else if (this.getOrientation() === 'BTT') {
            return this.getToggleButton('up');
        }
    };
    getRightComponent = () => {
        if (this.getOrientation() === 'TTB' || this.getOrientation() === 'BTT') {
            return this.getSettingsButton();
        }
    };
    getBottomLeftComponent = () => {
        if (this.getOrientation() === 'LTR') {
            return this.getDisableButton('right');
        } else if (this.getOrientation() === 'BTT') {
            return this.getReverseButton('down');
        }
    };
    getBottomComponent = () => {
        if (this.getOrientation() === 'LTR' || this.getOrientation() === 'RTL') {
            return this.getSettingsButton();
        }
    };
    getBottomRightComponent = () => {
        if (this.getOrientation() === 'RTL') {
            return this.getDisableButton('left');
        } else if (this.getOrientation() === 'BTT') {
            return this.getDisableButton('up');
        }
    };
    render() {
        return (
            <div>
                <FilterGrid
                    direction={
                        this.getOrientation() === 'RTL' || this.getOrientation() === 'LTR' ? 'horizontal' : 'vertical'
                    }
                    top={this.getTopComponent() || <span />}
                    topLeft={this.getTopLeftComponent()}
                    topRight={this.getTopRightComponent()}
                    left={this.getLeftComponent()}
                    center={this.getCenterComponent()}
                    right={this.getRightComponent()}
                    bottomLeft={this.getBottomLeftComponent()}
                    bottom={this.getBottomComponent()}
                    bottomRight={this.getBottomRightComponent()}
                />
                {/*onBlur={() => {this.setState({checked: false})}}*/}
            </div>
        );
    }
}
